import { norm } from "./texto.ts";
/**
 * Agenda: estados de la cita, solapes, horario de atención, huecos libres y dictado de citas.
 * Hora de Ecuador continental: UTC−5 todo el año (no hay horario de verano).
 */
export const DESFASE_EC_MIN = -5 * 60;

export const ESTADOS_CITA = ["agendada", "confirmada", "atendida", "no_asistio", "cancelada"] as const;
export type EstadoCita = (typeof ESTADOS_CITA)[number];
export const NOMBRE_ESTADO_CITA: Readonly<Record<EstadoCita, string>> = {
  agendada: "Agendada", confirmada: "Confirmada", atendida: "Atendida", no_asistio: "No asistió", cancelada: "Cancelada",
};
/** Transiciones permitidas: una cita atendida o cancelada ya no vuelve atrás (se agenda otra). */
export const TRANSICIONES: Readonly<Record<EstadoCita, readonly EstadoCita[]>> = {
  agendada: ["confirmada", "atendida", "no_asistio", "cancelada"],
  confirmada: ["agendada", "atendida", "no_asistio", "cancelada"],
  atendida: [], no_asistio: ["agendada"], cancelada: [],
};
export const DURACIONES = [15, 20, 30, 45, 60, 90, 120] as const;

/** Horario por día de la semana (0 = domingo). null = cerrado. Horas "HH:MM" locales. */
export type Horario = Readonly<Record<number, readonly [string, string] | null>>;
export const HORARIO_BASE: Horario = { 0: null, 1: ["08:00", "18:00"], 2: ["08:00", "18:00"], 3: ["08:00", "18:00"], 4: ["08:00", "18:00"], 5: ["08:00", "18:00"], 6: ["08:00", "13:00"] };

/* ---------- conversión de horas locales ---------- */

/** "2026-10-05" + "09:30" (hora de Ecuador) → instante UTC. */
export function aInstante(fecha: string, hora: string): Date {
  const [a, m, d] = fecha.split("-").map(Number), [h, mi] = hora.split(":").map(Number);
  return new Date(Date.UTC(a, m - 1, d, h, mi) - DESFASE_EC_MIN * 60000);
}
/** Instante → { fecha, hora, diaSemana } en hora de Ecuador. */
export function aLocal(t: Date): { fecha: string; hora: string; diaSemana: number } {
  const l = new Date(t.getTime() + DESFASE_EC_MIN * 60000);
  return { fecha: l.toISOString().slice(0, 10), hora: l.toISOString().slice(11, 16), diaSemana: l.getUTCDay() };
}
export function sumarDias(fecha: string, n: number): string {
  const d = new Date(fecha + "T12:00:00Z"); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10);
}
/** Lunes de la semana de `fecha`. */
export function inicioSemana(fecha: string): string {
  const dia = new Date(fecha + "T12:00:00Z").getUTCDay();
  return sumarDias(fecha, dia === 0 ? -6 : 1 - dia);
}

const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
/** "jueves 8 de octubre" */
export function fechaLarga(fecha: string): string {
  const d = new Date(fecha + "T12:00:00Z");
  return `${DIAS[d.getUTCDay()]} ${d.getUTCDate()} de ${MESES[d.getUTCMonth()]}`;
}
/** "10:30" → "10:30 a. m." */
export function horaLarga(hora: string): string {
  const [h, m] = hora.split(":").map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "a. m." : "p. m."}`;
}

/* ---------- validación ---------- */

export interface Intervalo { inicio: Date; fin: Date }
export const seSolapan = (a: Intervalo, b: Intervalo) => a.inicio < b.fin && b.inicio < a.fin;

/** Motivos por los que no se puede agendar en ese horario (vacío = se puede). */
export function problemasHorario(inicio: Date, duracionMin: number, horario: Horario = HORARIO_BASE): string[] {
  const p: string[] = [];
  const l = aLocal(inicio), fin = aLocal(new Date(inicio.getTime() + duracionMin * 60000));
  const h = horario[l.diaSemana];
  if (!h) { const d = DIAS[l.diaSemana]; p.push(`La clínica no atiende los ${d.endsWith("s") ? d : d + "s"}.`); }
  else {
    if (l.hora < h[0]) p.push(`La atención empieza a las ${h[0]}.`);
    if (fin.fecha !== l.fecha || fin.hora > h[1]) p.push(`La cita termina después del cierre (${h[1]}).`);
  }
  if (Number(l.hora.slice(3)) % 5 !== 0) p.push("Usa horas en múltiplos de 5 minutos.");
  return p;
}

/** Primeros huecos libres de un día para una duración, cada 15 minutos. */
export function huecosLibres(fecha: string, duracionMin: number, ocupadas: Intervalo[], horario: Horario = HORARIO_BASE, max = 6): string[] {
  const d = new Date(fecha + "T12:00:00Z").getUTCDay();
  const h = horario[d];
  if (!h) return [];
  const out: string[] = [];
  let t = aInstante(fecha, h[0]);
  const cierre = aInstante(fecha, h[1]);
  while (t.getTime() + duracionMin * 60000 <= cierre.getTime() && out.length < max) {
    const iv = { inicio: t, fin: new Date(t.getTime() + duracionMin * 60000) };
    if (!ocupadas.some((o) => seSolapan(o, iv))) out.push(aLocal(t).hora);
    t = new Date(t.getTime() + 15 * 60000);
  }
  return out;
}

const dow = (fecha: string) => { const d = new Date(fecha + "T12:00:00Z").getUTCDay(); return d === 0 ? 7 : d; };
const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const fechaOk = (f: string) => /^\d{4}-\d{2}-\d{2}$/.test(f) && !Number.isNaN(Date.parse(f + "T12:00:00Z")) && new Date(f + "T12:00:00Z").toISOString().slice(0, 10) === f;

/* ---------- dictado ---------- */

const D_DIAS = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
const D_MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const D_NUM: Record<string, number> = { una: 1, un: 1, uno: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10, once: 11, doce: 12 };

export interface CitaDictada { nombre: string; fecha: string | null; hora: string | null; duracion: number | null; motivo: string }

/**
 * "Agenda a María Fernanda el jueves a las 10 para control" · "cita para Mateo mañana a las 3 de la tarde por una hora, endodoncia"
 * Devuelve lo que entendió; lo que falta queda en null para que la pantalla lo pida.
 */
export function interpretarCita(texto: string, hoy: string): CitaDictada {
  const c = norm(texto).replace(/[.,;]/g, " ").replace(/\s+/g, " ").trim();
  let fecha: string | null = null;
  const sinFranja = c.replace(/\b(?:de|en|por) la manana\b/g, "");
  if (/\bpasado manana\b/.test(c)) fecha = sumarDias(hoy, 2);
  else if (/\bmanana\b/.test(sinFranja)) fecha = sumarDias(hoy, 1);
  else if (/\bhoy\b/.test(c)) fecha = hoy;
  const mDia = c.match(/\b(?:el |este |proximo |el proximo )?(lunes|martes|miercoles|jueves|viernes|sabado|domingo)\b/);
  if (mDia) {
    const objetivo = D_DIAS.indexOf(mDia[1]) + 1;
    let delta = (objetivo - dow(hoy) + 7) % 7;
    if (delta === 0) delta = 7;
    fecha = sumarDias(hoy, delta);
  }
  const mFecha = c.match(/\b(?:el )?(\d{1,2}) de (enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\b/) ?? c.match(/\bel (\d{1,2})\b(?! de la)(?!:)/);
  if (mFecha && !/a las \d/.test(mFecha[0])) {
    const dia = Number(mFecha[1]);
    const [a, m] = hoy.split("-").map(Number);
    let mes = mFecha[2] ? D_MESES.indexOf(mFecha[2]) + 1 : m, anio = a;
    let f = `${anio}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
    if (fechaOk(f) && f < hoy) { if (mFecha[2]) anio++; else { mes = mes === 12 ? 1 : mes + 1; if (mes === 1) anio++; } f = `${anio}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`; }
    if (fechaOk(f)) fecha = f;
  }
  let hora: string | null = null;
  const mHora = c.match(/\ba las? (\d{1,2}|una|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce)(?::(\d{2})| y (media|cuarto))?(?: (?:de la|en la|por la) (manana|tarde|noche))?\b/)
    ?? c.match(/\b(\d{1,2}):(\d{2})\b/);
  if (mHora) {
    let h = /^\d+$/.test(mHora[1]) ? Number(mHora[1]) : D_NUM[mHora[1]];
    const min = mHora[2] ? Number(mHora[2]) : mHora[3] === "media" ? 30 : mHora[3] === "cuarto" ? 15 : 0;
    const franja = mHora[4];
    if ((franja === "tarde" || franja === "noche") && h < 12) h += 12;
    else if (!franja && h >= 1 && h <= 6) h += 12; // "a las 3" en consultorio se entiende de la tarde
    if (h >= 0 && h < 24 && min < 60) hora = hhmm(h * 60 + min);
  }
  let duracion: number | null = null;
  const mDur = c.match(/\b(?:por|durante|de) (una|media|\d+|dos|tres) (hora|horas|minutos?)\b/) ?? c.match(/\b(media hora|hora y media)\b/);
  if (mDur) {
    if (mDur[1] === "media hora") duracion = 30;
    else if (mDur[1] === "hora y media") duracion = 90;
    else if (mDur[1] === "media") duracion = 30;
    else { const n = /^\d+$/.test(mDur[1]) ? Number(mDur[1]) : D_NUM[mDur[1]]; duracion = mDur[2].startsWith("hora") ? n * 60 : n; }
  }
  // nombre: después de "agenda(r) a" / "cita para|de|con" hasta la primera palabra de tiempo
  const mNombre = c.match(/\b(?:agenda|agendar|agendale|agende|cita|citar|programa|reserva)(?:r)?(?: una cita)?(?: a| para| de| con)? ([a-z ]+?)(?= (?:el|este|proximo|hoy|manana|pasado|a las|para|por|durante|lunes|martes|miercoles|jueves|viernes|sabado|domingo)\b|$)/);
  let nombre = mNombre ? mNombre[1].trim() : "";
  if (/^(la|el|una|un|cita)$/.test(nombre)) nombre = "";
  const cm = nombre ? c.replace(new RegExp(`\\b(?:a|para|de|con) ${nombre}\\b`), " ") : c;
  const mMotivo = cm.match(/\bpara (?!el |la |las |los |manana|hoy)(?:un |una )?(control|limpieza|profilaxis|endodoncia|extraccion|exodoncia|resina|ortodoncia|valoracion|consulta|revision|blanqueamiento|cirugia|emergencia|urgencia|[a-z ]{3,40}?)(?= (?:el|a las|manana|hoy|pasado|por|durante)\b|$)/)
    ?? cm.match(/ (control|limpieza|profilaxis|endodoncia|extraccion|exodoncia|ortodoncia|valoracion|blanqueamiento|cirugia|emergencia|urgencia)$/);
  const motivo = mMotivo ? mMotivo[1].trim() : "";
  return { nombre, fecha, hora, duracion, motivo: motivo.charAt(0).toUpperCase() + motivo.slice(1) };
}


/**
 * Reparte las citas de un día en columnas para que las que coinciden en la hora se vean una al lado de la otra
 * (en lugar de taparse). Cada cita recibe su columna y cuántas columnas tiene su grupo de citas encimadas.
 * Los minutos son desde la medianoche; una cita que empieza justo cuando otra termina no se encima.
 */
export function columnasDeCitas<T extends { id: string; inicio: number; fin: number }>(citas: readonly T[]): Map<string, { col: number; cols: number }> {
  const orden = [...citas].sort((a, b) => a.inicio - b.inicio || a.fin - b.fin || a.id.localeCompare(b.id));
  const salida = new Map<string, { col: number; cols: number }>();
  let grupo: { id: string; col: number }[] = [], finGrupo = -1, columnas: number[] = [];
  const cerrar = () => { for (const g of grupo) salida.set(g.id, { col: g.col, cols: Math.max(1, columnas.length) }); grupo = []; columnas = []; };
  for (const c of orden) {
    if (grupo.length && c.inicio >= finGrupo) cerrar();           // ya no se encima con el grupo anterior
    let col = columnas.findIndex((fin) => fin <= c.inicio);        // primera columna libre
    if (col < 0) { col = columnas.length; columnas.push(c.fin); } else columnas[col] = c.fin;
    grupo.push({ id: c.id, col });
    finGrupo = grupo.length === 1 ? c.fin : Math.max(finGrupo, c.fin);
  }
  cerrar();
  return salida;
}
